var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import Autocomplete from "react-google-autocomplete";
import { biasAwayFromNYBounds, GOOGLE_API_KEY, parseAddress, } from "../../services/googleAddress";
function parseStoredAddress(fullAddress) {
    if (!fullAddress || !fullAddress.trim())
        return null;
    var match = fullAddress.match(/^(.+?),\s*(.+?),\s*(\w+)\s+(\d+)$/);
    if (!match)
        return null;
    var street = match[1], city = match[2], state = match[3], zip = match[4];
    return { street: street, city: city, state: state, zip: zip };
}
export default function AutoAddressMultifield(_a) {
    var field = _a.field, onChange = _a.onChange, onBlur = _a.onBlur;
    var fieldConfig = ((field === null || field === void 0 ? void 0 : field.config) || {});
    // On mount, if we already have a stored fieldValue, restore parsed address fields
    var restoredAddress = (field === null || field === void 0 ? void 0 : field.fieldValue) ? parseStoredAddress(field.fieldValue) : null;
    var _b = useState(false), blurred = _b[0], setBlurred = _b[1];
    var _c = useState(!!restoredAddress), isValidSelection = _c[0], setIsValidSelection = _c[1];
    var _d = useState((field === null || field === void 0 ? void 0 : field.fieldValue) || ""), inputValue = _d[0], setInputValue = _d[1];
    var _e = useState(restoredAddress || { street: "", city: "", state: "", zip: "" }), addressFields = _e[0], setAddressFields = _e[1];
    var invalidate = function () {
        setInputValue("");
        setIsValidSelection(false);
        setAddressFields({ street: "", city: "", state: "", zip: "" });
        onChange("", true);
        onBlur();
    };
    var handlePlaceSelect = function (place) {
        if (!(place === null || place === void 0 ? void 0 : place.address_components)) {
            setIsValidSelection(false);
            return;
        }
        var hasStreetNumber = place.address_components.some(function (c) {
            return c.types.includes("street_number");
        });
        if (!hasStreetNumber) {
            invalidate();
            return;
        }
        var parsedAddress = parseAddress(place);
        if (field.config.NotNY && parsedAddress.state === "NY") {
            invalidate();
            return;
        }
        var fullAddress = "".concat(parsedAddress.street, ", ").concat(parsedAddress.city, ", ").concat(parsedAddress.state, " ").concat(parsedAddress.zip);
        setIsValidSelection(true);
        setInputValue(fullAddress);
        setAddressFields({
            street: parsedAddress.street,
            city: parsedAddress.city || "",
            state: parsedAddress.state || "",
            zip: parsedAddress.zip || "",
        });
        onChange(fullAddress, true);
        setBlurred(true);
        onBlur();
    };
    var handleBlur = function () {
        setBlurred(true);
        if (!inputValue.trim() || !isValidSelection) {
            invalidate();
            return;
        }
        onChange(inputValue, true);
        onBlur();
    };
    var handleInputChange = function (e) {
        var value = e.currentTarget.value;
        setInputValue(value);
        setIsValidSelection(false);
        setAddressFields({ street: "", city: "", state: "", zip: "" });
        onChange(value, false);
        if (!value.trim()) {
            onChange("", false);
        }
    };
    var showError = !field.isValid && blurred;
    var showSuccess = field.isValid && blurred && field.fieldValue !== "";
    var fieldClass = [
        fieldConfig.className || "",
        showError ? "field-error" : "",
        showSuccess ? "field-success" : "",
    ]
        .filter(Boolean)
        .join(" ")
        .trim();
    useEffect(function () {
        var btn = document.querySelector('.btn-next');
        var hasError = !!document.querySelector('.field-error');
        if (btn) {
            btn.style.display = hasError ? 'none' : '';
        }
    }, [addressFields.street, addressFields.city, addressFields.state, addressFields.zip]);
    return (_jsxs("div", { className: "textbox", children: [fieldConfig.labelText ? (_jsx("label", { htmlFor: fieldConfig.fieldId, children: fieldConfig.labelText })) : (_jsx("label", { htmlFor: fieldConfig.fieldId, className: "visually-hidden", children: fieldConfig.placeHolder || "Property Address" })), _jsx(Autocomplete, { apiKey: GOOGLE_API_KEY, options: {
                    types: ["address"],
                    componentRestrictions: { country: "us" },
                    bounds: biasAwayFromNYBounds,
                    strictBounds: true,
                }, onPlaceSelected: handlePlaceSelect, value: inputValue, onChange: handleInputChange, onBlur: handleBlur, placeholder: fieldConfig.placeHolder || "Enter property address", className: fieldClass, autoComplete: fieldConfig.autoComplete || "off", autoCorrect: fieldConfig.autoCorrect || "off", spellCheck: fieldConfig.spellCheck || false, "aria-invalid": showError ? "true" : "false" }), isValidSelection && (_jsxs("div", { className: "address-multifield-fields row", children: [_jsxs("div", { className: "col-lg-6", children: [_jsx("label", { children: "Street Address" }), _jsx("input", { type: "text", value: addressFields.street, onChange: function (e) {
                                    return setAddressFields(function (fields) { return (__assign(__assign({}, fields), { street: e.target.value })); });
                                }, className: !addressFields.street ? "field-error" : "field-success" }), !addressFields.street && (_jsx("span", { className: "error-msg", children: "Required" }))] }), _jsxs("div", { className: "col-lg-6", children: [_jsx("label", { children: "City" }), _jsx("input", { type: "text", value: addressFields.city, onChange: function (e) {
                                    return setAddressFields(function (fields) { return (__assign(__assign({}, fields), { city: e.target.value })); });
                                }, className: !addressFields.city ? "field-error" : "field-success" }), !addressFields.city && (_jsx("span", { className: "error-msg", children: "Required" }))] }), _jsxs("div", { className: "col-lg-6", children: [_jsx("label", { children: "State" }), _jsx("input", { type: "text", value: addressFields.state, onChange: function (e) {
                                    return setAddressFields(function (fields) { return (__assign(__assign({}, fields), { state: e.target.value })); });
                                }, className: !addressFields.state ? "field-error" : "field-success" }), !addressFields.state && (_jsx("span", { className: "error-msg", children: "Required" }))] }), _jsxs("div", { className: "col-lg-6", children: [_jsx("label", { children: "Zip Code" }), _jsx("input", { type: "text", value: addressFields.zip, onChange: function (e) {
                                    return setAddressFields(function (fields) { return (__assign(__assign({}, fields), { zip: e.target.value })); });
                                }, className: !addressFields.zip ? "field-error" : "field-success" }), !addressFields.zip && (_jsx("span", { className: "error-msg", children: "Required" }))] })] }))] }));
}
